// Save as: src/pages/analytics/PentestAnalytics.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  Divider,
} from '@mui/material';
import { alpha , darken, lighten} from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';

// Chart.js (via react-chartjs-2)
// npm i chart.js react-chartjs-2 chartjs-plugin-datalabels
import { Line, Bar, Doughnut, Scatter } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Tooltip as ChartTooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';

import InsightsIcon from '@mui/icons-material/Insights';
import TimelineIcon from '@mui/icons-material/Timeline';
import AssessmentIcon from '@mui/icons-material/Assessment';
import PublicIcon from '@mui/icons-material/Public';
import PhoneIcon from '@mui/icons-material/Phone';
import LockIcon from '@mui/icons-material/Lock';
import LanguageIcon from '@mui/icons-material/Language';
import RefreshIcon from '@mui/icons-material/Refresh';
import QueryStatsIcon from '@mui/icons-material/QueryStats';

import ErrorNotification from '../../components/ErrorNotification';
import { BASEURL ,PRIMARYCOLOR,SECONDARYCOLOR,logoURL,COMPANY } from '../../Constants';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ChartTooltip,
  Legend,
  ArcElement,
  ChartDataLabels,
);


const brandColor = PRIMARYCOLOR;

// === Helpers ===
const getGrade = (score) => {
  if (score === null || score === undefined) return '?';
  if (score < 60) return 'F';
  if (score < 70) return 'D';
  if (score < 80) return 'C';
  if (score < 90) return 'B';
  if (score <= 95) return 'A-';
  return 'A+';
};

const getGradeColor = (grade) => {
  switch (grade) {
    case 'A+':
    case 'A-':
      return '#16a34a';
    case 'B':
      return '#2563eb';
    case 'C':
      return '#f59e0b';
    case 'D':
      return '#eab308';
    case 'F':
      return '#ef4444';
    default:
      return '#9e9e9e';
  }
};

const TYPE_META = {
  external: { label: 'External', icon: PublicIcon, color: '#10b981' },
  web: { label: 'Web', icon: LanguageIcon, color: '#3b82f6' },
  api: { label: 'API', icon: LanguageIcon, color: '#06b6d4' },
  vishing: { label: 'Vishing', icon: PhoneIcon, color: '#f59e0b' },
  darkweb: { label: 'Darkweb', icon: LockIcon, color: '#8b5cf6' },
};
const TYPE_KEYS = ['external', 'web', 'api', 'vishing', 'darkweb'];

// ===== Period helpers =====
const startOfWeek = (date) => {
  const d = new Date(date);
  const day = (d.getDay() + 6) % 7; // Monday=0
  d.setDate(d.getDate() - day);
  d.setHours(0, 0, 0, 0);
  return d;
};
const startOfMonth = (date) => {
  const d = new Date(date);
  d.setDate(1);
  d.setHours(0, 0, 0, 0);
  return d;
};
const startOfQuarter = (date) => {
  const d = new Date(date);
  const q = Math.floor(d.getMonth() / 3) * 3;
  d.setMonth(q, 1);
  d.setHours(0, 0, 0, 0);
  return d;
};
const startOfYear = (date) => {
  const d = new Date(date);
  d.setMonth(0, 1);
  d.setHours(0, 0, 0, 0);
  return d;
};

const periodStartFns = {
  week: startOfWeek,
  month: startOfMonth,
  quarter: startOfQuarter,
  year: startOfYear,
};

const formatPeriodLabel = (granularity, dateObj) => {
  const d = new Date(dateObj);
  const y = d.getFullYear();
  const m = d.getMonth();
  if (granularity === 'week') {
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }
  if (granularity === 'month') {
    return d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
  }
  if (granularity === 'quarter') {
    const q = Math.floor(m / 3) + 1;
    return `Q${q} ${y}`;
  }
  return String(y);
};

// Aggregate pentests into rows by period
const aggregateByPeriod = (data, granularity, filterType = 'all') => {
  const startFn = periodStartFns[granularity] || startOfMonth;
  const map = new Map();

  data.forEach((p) => {
    const createdAt = p.created_at || p.createdAt || p.createdAtUtc || p.createdAtISO;
    if (!createdAt) return;
    const tKey = String(p.type || 'external').toLowerCase();
    if (filterType !== 'all' && tKey !== filterType) return;
    const dt = new Date(createdAt);
    if (isNaN(dt.getTime())) return;
    const keyDate = startFn(dt);
    const key = keyDate.getTime();
    if (!map.has(key)) {
      map.set(key, { date: new Date(keyDate), count: 0, sumRisk: 0, riskN: 0, findings: 0, byType: {} });
    }
    const row = map.get(key);
    row.count += 1;
    const r = Number(p.risk_score);
    if (Number.isFinite(r)) {
      row.sumRisk += r;
      row.riskN += 1;
    }
    const findings = Number(p.exploits_count ?? 0);
    row.findings += Number.isFinite(findings) ? findings : 0;
    const current = row.byType[tKey] || { count: 0, findings: 0, sumRisk: 0, riskN: 0 };
    current.count += 1;
    current.findings += Number.isFinite(findings) ? findings : 0;
    if (Number.isFinite(r)) { current.sumRisk += r; current.riskN += 1; }
    row.byType[tKey] = current;
  });

  const rows = Array.from(map.values()).sort((a, b) => a.date - b.date);
  return rows.map((r) => ({
    label: formatPeriodLabel(granularity, r.date),
    date: r.date,
    count: r.count,
    findings: r.findings,
    avgRisk: r.riskN ? r.sumRisk / r.riskN : null,
    byType: TYPE_KEYS.reduce((acc, k) => {
      const t = r.byType[k] || { count: 0, findings: 0, sumRisk: 0, riskN: 0 };
      acc[k] = { count: t.count, findings: t.findings, avgRisk: t.riskN ? t.sumRisk / t.riskN : null };
      return acc;
    }, {}),
  }));
};

// Overall totals + by-type
const computeTotals = (data) => {
  const totalPentests = data.length;
  let sumRisk = 0, riskN = 0, totalFindings = 0;
  const byType = {};
  TYPE_KEYS.forEach((k) => (byType[k] = { count: 0, findings: 0, sumRisk: 0, riskN: 0 }));

  data.forEach((p) => {
    const tKey = (p.type || 'external').toLowerCase();
    if (!byType[tKey]) byType[tKey] = { count: 0, findings: 0, sumRisk: 0, riskN: 0 };
    byType[tKey].count += 1;

    const r = Number(p.risk_score);
    if (Number.isFinite(r)) {
      sumRisk += r; riskN += 1; byType[tKey].sumRisk += r; byType[tKey].riskN += 1;
    }
    const findings = Number(p.exploits_count ?? 0);
    totalFindings += Number.isFinite(findings) ? findings : 0;
    byType[tKey].findings += Number.isFinite(findings) ? findings : 0;
  });

  const byTypeFinal = Object.fromEntries(
    Object.entries(byType).map(([k, v]) => [k, { count: v.count, findings: v.findings, avgRisk: v.riskN ? v.sumRisk / v.riskN : null }])
  );

  return { totalPentests, avgRisk: riskN ? sumRisk / riskN : null, totalFindings, byType: byTypeFinal };
};

// Grade distribution (A+/A-/B/C/D/F)
const computeGradeDistribution = (data) => {
  const buckets = { 'A+': 0, 'A-': 0, B: 0, C: 0, D: 0, F: 0 };
  data.forEach((p) => {
    const g = getGrade(p?.risk_score);
    if (buckets[g] == null) return; // ignore '?'
    buckets[g] += 1;
  });
  return buckets;
};

// Small stat card
const StatCard = ({ icon, title, value, subtitle, gradient = false }) => (
  <Card elevation={0} sx={{ borderRadius: 3, height: '100%', background: gradient ? `linear-gradient(135deg, ${brandColor} 0%, ${darken(brandColor, 0.4)} 100%)` : (t) => alpha(t.palette.background.paper, 1), color: gradient ? 'white' : 'inherit', boxShadow: gradient ? '0 10px 40px rgba(0,0,0,0.15)' : 'none' }}>
    <CardContent>
      <Stack direction="row" spacing={2} alignItems="center">
        <Avatar sx={{ bgcolor: gradient ? alpha('#fff', 0.2) : alpha(brandColor, 0.1) }}>{icon}</Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography variant="overline" sx={{ opacity: gradient ? 0.9 : 0.7 }}>{title}</Typography>
          <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1 }}>{value}</Typography>
          {subtitle && <Typography variant="body2" sx={{ opacity: gradient ? 0.9 : 0.7 }}>{subtitle}</Typography>}
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

const PentestAnalytics = () => {
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pentests, setPentests] = useState([]);

  const [granularity, setGranularity] = useState('month'); // week | month | quarter | year
  const [windowSize, setWindowSize] = useState(12); // how many periods to show
  const [typeFilter, setTypeFilter] = useState('all'); // 'all' | one of TYPE_KEYS

  const fetchPentests = () => {
    const token = sessionStorage.getItem('token');
    if (!token) { navigate('/'); return; }
    setLoading(true);
    fetch(`${BASEURL}/pentests`, { method: 'GET', headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token } })
      .then((r) => r.json())
      .then((res) => {
        if (res.error) { setError(res.message || 'Error fetching pentests'); setPentests([]); }
        else { setPentests(res.pentests || []); setError(null); }
      })
      .catch(() => setError('Error fetching pentests'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchPentests(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { const defaults = { week: 12, month: 12, quarter: 8, year: 5 }; setWindowSize(defaults[granularity] || 12); }, [granularity]);

  const totals = useMemo(() => computeTotals(pentests), [pentests]);
  const seriesAll = useMemo(() => aggregateByPeriod(pentests, granularity, 'all'), [pentests, granularity]);
  const seriesFiltered = useMemo(() => aggregateByPeriod(pentests, granularity, typeFilter), [pentests, granularity, typeFilter]);

  const baseSeries = typeFilter === 'all' ? seriesAll : seriesFiltered;
  const slicedSeries = useMemo(() => { if (!windowSize || baseSeries.length <= windowSize) return baseSeries; return baseSeries.slice(baseSeries.length - windowSize); }, [baseSeries, windowSize]);

  const gradeDist = useMemo(() => computeGradeDistribution(pentests), [pentests]);

  // ========== Chart.js datasets ==========
  const labels = slicedSeries.map((r) => r.label);

  // Avg Risk trend (filtered by type when typeFilter != 'all')
  const avgRiskData = { labels, datasets: [{ label: 'Avg Risk', data: slicedSeries.map((r) => (r.avgRisk != null ? Math.round(r.avgRisk) : null)), borderWidth: 2, tension: 0.35, pointRadius: 4, spanGaps: true, backgroundColor: alpha(brandColor, 0.2), borderColor: brandColor }] };
  const avgRiskOptions = { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'top' }, tooltip: { callbacks: { label: (ctx) => `Avg Risk: ${ctx.parsed.y}/100 (${getGrade(ctx.parsed.y)})` } }, datalabels: { align: 'top', offset: 4, formatter: (v) => (v == null ? '' : getGrade(v)), color: (ctx) => getGradeColor(getGrade(ctx.dataset.data[ctx.dataIndex])), font: { weight: '800' } } }, scales: { y: { min: 0, max: 100, ticks: { stepSize: 20 } } } };

  // Pentests over time (stacked by type when "all")
  const stackedByTypeCounts = () => {
    const labelsAll = (typeFilter === 'all' ? seriesAll : seriesFiltered).map((r) => r.label);
    const rows = typeFilter === 'all' ? seriesAll : seriesFiltered;
    const ds = (typeFilter === 'all' ? TYPE_KEYS : [typeFilter]).map((k) => ({
      label: TYPE_META[k].label,
      data: rows.map((r) => (r.byType?.[k]?.count ?? (typeFilter === 'all' ? 0 : r.count))),
      backgroundColor: alpha(TYPE_META[k].color, 0.5),
      borderColor: TYPE_META[k].color,
      borderWidth: 1,
      stack: 'counts',
    }));
    return { labels: labelsAll.slice(-windowSize), datasets: ds };
  };
  const countsByTypeData = stackedByTypeCounts();
  const countsByTypeOptions = { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'top' } }, scales: { x: { stacked: typeFilter === 'all' }, y: { stacked: typeFilter === 'all', beginAtZero: true, ticks: { precision: 0 } } } };

  // Findings over time (stacked by type when "all")
  const stackedByTypeFindings = () => {
    const labelsAll = (typeFilter === 'all' ? seriesAll : seriesFiltered).map((r) => r.label);
    const rows = typeFilter === 'all' ? seriesAll : seriesFiltered;
    const ds = (typeFilter === 'all' ? TYPE_KEYS : [typeFilter]).map((k) => ({
      label: TYPE_META[k].label,
      data: rows.map((r) => (r.byType?.[k]?.findings ?? (typeFilter === 'all' ? 0 : r.findings))),
      backgroundColor: alpha(TYPE_META[k].color, 0.5),
      borderColor: TYPE_META[k].color,
      borderWidth: 1,
      stack: 'findings',
    }));
    return { labels: labelsAll.slice(-windowSize), datasets: ds };
  };
  const findingsByTypeData = stackedByTypeFindings();
  const findingsByTypeOptions = { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'top' } }, scales: { x: { stacked: typeFilter === 'all' }, y: { stacked: typeFilter === 'all', beginAtZero: true, ticks: { precision: 0 } } } };

  // Average risk by type (overall)
  const avgRiskByType = {
    labels: TYPE_KEYS.map((k) => TYPE_META[k].label),
    datasets: [{ label: 'Avg Risk', data: TYPE_KEYS.map((k) => (totals.byType?.[k]?.avgRisk != null ? Math.round(totals.byType[k].avgRisk) : null)), backgroundColor: TYPE_KEYS.map((k) => alpha(TYPE_META[k].color, 0.5)), borderColor: TYPE_KEYS.map((k) => TYPE_META[k].color), borderWidth: 1 }],
  };
  const avgRiskByTypeOptions = { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false }, datalabels: { anchor: 'end', align: 'top', formatter: (v) => (v == null ? '' : `${v} (${getGrade(v)})`), color: (ctx) => getGradeColor(getGrade(ctx.dataset.data[ctx.dataIndex])), font: { weight: '800' } } }, scales: { y: { min: 0, max: 100 } } };

  // Findings by type (overall)
  const findingsByType = {
    labels: TYPE_KEYS.map((k) => TYPE_META[k].label),
    datasets: [{ label: 'Findings', data: TYPE_KEYS.map((k) => totals.byType?.[k]?.findings ?? 0), backgroundColor: TYPE_KEYS.map((k) => alpha(TYPE_META[k].color, 0.5)), borderColor: TYPE_KEYS.map((k) => TYPE_META[k].color), borderWidth: 1 }],
  };
  //const findingsByTypeOptions = { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { y: { beginAtZero: true, ticks: { precision: 0 } } } };

  // Grade distribution (doughnut)
  const gradeDistData = {
    labels: Object.keys(gradeDist),
    datasets: [{ data: Object.values(gradeDist), backgroundColor: Object.keys(gradeDist).map((g) => alpha(getGradeColor(g), 0.7)), borderColor: Object.keys(gradeDist).map((g) => getGradeColor(g)), borderWidth: 1 }],
  };

  // Risk vs Findings scatter (colored by type)
  const scatterData = {
    datasets: TYPE_KEYS.map((k) => ({
      label: TYPE_META[k].label,
      data: pentests.filter((p) => (p.type || 'external').toLowerCase() === k).map((p) => ({ x: Number(p.risk_score) || 0, y: Number(p.exploits_count) || 0 })),
      backgroundColor: alpha(TYPE_META[k].color, 0.6),
      borderColor: TYPE_META[k].color,
      pointRadius: 4,
    })),
  };
  const scatterOptions = { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'top' }, tooltip: { callbacks: { label: (ctx) => `Risk ${ctx.raw.x}/100 • Findings ${ctx.raw.y}` } } }, scales: { x: { min: 0, max: 100, title: { display: true, text: 'Risk Score' } }, y: { beginAtZero: true, title: { display: true, text: 'Findings' } } } };

  // Quick insights
  const insights = useMemo(() => {
    const busiest = seriesAll.reduce((acc, r) => (r.count > (acc?.count || 0) ? r : acc), null);
    const byType = totals.byType || {};
    const typeMostFindings = Object.keys(byType).reduce((acc, k) => (byType[k].findings > (byType[acc]?.findings || 0) ? k : acc), TYPE_KEYS[0]);
    const typeHighestRisk = Object.keys(byType).reduce((acc, k) => ((byType[k].avgRisk || 0) > (byType[acc]?.avgRisk || 0) ? k : acc), TYPE_KEYS[0]);
    return {
      busiestLabel: busiest ? `${busiest.label} • ${busiest.count} pentests` : '—',
      mostFindingsLabel: `${TYPE_META[typeMostFindings].label} • ${byType[typeMostFindings]?.findings ?? 0} findings`,
      highestRiskLabel: `${TYPE_META[typeHighestRisk].label} • ${byType[typeHighestRisk]?.avgRisk ? Math.round(byType[typeHighestRisk].avgRisk) : '—'}/100 (${getGrade(byType[typeHighestRisk]?.avgRisk)})`,
    };
  }, [seriesAll, totals]);

  // ====== UI ======
  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: (t) => alpha(t.palette.grey[100], 0.8), backgroundImage: `radial-gradient(${alpha('#cfe8ff', 0.5)} 1px, transparent 1px)`, backgroundSize: '16px 16px' }}>
      <ErrorNotification errorMessage={error} />

      <Box sx={{ flexGrow: 1, p: 3 }}>
        {/* Header */}
        <Card sx={{ mb: 3, p: 3, borderRadius: 3, background: `linear-gradient(135deg, ${brandColor} 0%, ${darken(brandColor, 0.4)} 100%)`, color: 'white', boxShadow: '0 10px 40px rgba(0,0,0,0.15)' }}>
          <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between" spacing={2}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1 }}>Pentest Analytics</Typography>
              <Typography sx={{ opacity: 0.9, mt: 1 }}>Explore volume, risk, grades, and findings over time — with breakdowns by pentest type.</Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap' }}>
                <Chip label={`Total Pentests: ${totals.totalPentests}`} variant="outlined" sx={{ borderColor: alpha('#fff', 0.6), color: 'white' }} />
                <Chip label={`Total Findings: ${totals.totalFindings}`} variant="outlined" sx={{ borderColor: alpha('#fff', 0.6), color: 'white' }} />
                <Chip label={`Avg Risk: ${totals.avgRisk != null ? Math.round(totals.avgRisk) : '—'}/100 (${getGrade(totals.avgRisk)})`} variant="outlined" sx={{ borderColor: alpha('#fff', 0.6), color: 'white' }} />
              </Stack>
            </Box>

            <Stack spacing={1} alignItems={{ xs: 'stretch', md: 'flex-end' }}>
              {/* Period Controls (bright white for contrast) */}
              <ToggleButtonGroup exclusive value={granularity} onChange={(_, val) => val && setGranularity(val)} size="small" sx={{
                bgcolor: alpha('#fff', 0.95), borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.15)',
                '& .MuiToggleButton-root': {
                  color: brandColor, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5, px: 1.5,
                  borderColor: 'transparent', '&:not(:last-of-type)': { borderRight: '1px solid ' + alpha('#000', 0.06) },
                },
                '& .Mui-selected': { bgcolor: 'white', border: '1.5px solid ' + alpha(brandColor, 0.6), color: brandColor },
              }}>
                <ToggleButton value="week">Week</ToggleButton>
                <ToggleButton value="month">Month</ToggleButton>
                <ToggleButton value="quarter">Quarter</ToggleButton>
                <ToggleButton value="year">Year</ToggleButton>
              </ToggleButtonGroup>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="caption" sx={{ color: 'white', opacity: 0.9 }}>Periods</Typography>
                  <Select size="small" value={windowSize} onChange={(e) => setWindowSize(Number(e.target.value))} sx={{ color: 'white', minWidth: 96, '& .MuiSelect-icon': { color: 'white' } }}>
                    {[6, 8, 10, 12, 16, 20, 24, 36].map((n) => (<MenuItem key={n} value={n}>{n}</MenuItem>))}
                  </Select>
                  <Tooltip title="Reload data"><IconButton onClick={fetchPentests} sx={{ color: 'white' }}><RefreshIcon /></IconButton></Tooltip>
                </Stack>

                {/* Type filter controls (also white) */}
                <ToggleButtonGroup exclusive value={typeFilter} onChange={(_, v) => v && setTypeFilter(v)} size="small" sx={{
                  bgcolor: alpha('#fff', 0.95), borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.15)',
                  '& .MuiToggleButton-root': { color: brandColor, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5, px: 1.25 },
                  '& .Mui-selected': { bgcolor: 'white', border: '1.5px solid ' + alpha(brandColor, 0.6), color: brandColor },
                }}>
                  <ToggleButton value="all">All</ToggleButton>
                  {TYPE_KEYS.map((k) => {
                    const MetaIcon = TYPE_META[k].icon;
                    return <ToggleButton key={k} value={k}><MetaIcon sx={{ mr: 0.5 }} />{TYPE_META[k].label}</ToggleButton>;
                  })}
                </ToggleButtonGroup>
              </Stack>
            </Stack>
          </Stack>
        </Card>

        {/* Overview cards */}
        {loading ? (
          <Box sx={{ p: 3 }}>{[...Array(4)].map((_, i) => (<Skeleton key={i} variant="rounded" height={120} sx={{ mb: 2 }} />))}</Box>
        ) : (
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={4}><StatCard icon={<InsightsIcon />} title="Total Pentests" value={totals.totalPentests} subtitle="All-time across all types" gradient /></Grid>
            <Grid item xs={12} md={4}><StatCard icon={<AssessmentIcon />} title="Total Findings" value={totals.totalFindings} subtitle="Sum of findings discovered" /></Grid>
            <Grid item xs={12} md={4}><StatCard icon={<TimelineIcon />} title="Average Risk" value={`${totals.avgRisk != null ? Math.round(totals.avgRisk) : '—'}/100`} subtitle={`Avg Grade: ${getGrade(totals.avgRisk)}`} /></Grid>
          </Grid>
        )}

        {/* Quick insights */}
        {!loading && (
          <Card sx={{ p: 2, borderRadius: 3, mb: 3 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', md: 'center' }}>
              <Chip icon={<QueryStatsIcon />} label={`Busiest period: ${insights.busiestLabel}`} />
              <Chip icon={<QueryStatsIcon />} label={`Most findings: ${insights.mostFindingsLabel}`} />
              <Chip icon={<QueryStatsIcon />} label={`Highest avg risk: ${insights.highestRiskLabel}`} />
            </Stack>
          </Card>
        )}

        {/* By Type summary cards */}
        {loading ? (
          <Box sx={{ p: 3 }}>{[...Array(5)].map((_, i) => (<Skeleton key={i} variant="rounded" height={96} sx={{ mb: 1 }} />))}</Box>
        ) : (
          <Card sx={{ p: 2.5, borderRadius: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>Totals by Type</Typography>
            <Grid container spacing={2}>
              {TYPE_KEYS.map((k) => (
                <Grid item xs={12} sm={6} md={2.4} key={k}>
                  <Card elevation={0} sx={{ borderRadius: 3, height: '100%' }}>
                    <CardContent>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Avatar sx={{ bgcolor: alpha(TYPE_META[k].color, 0.15) }}>{React.createElement(TYPE_META[k].icon)}</Avatar>
                        <Box>
                          <Typography variant="overline" sx={{ opacity: 0.7 }}>{TYPE_META[k].label}</Typography>
                          <Typography variant="h6" sx={{ fontWeight: 800 }}>{totals.byType?.[k]?.count ?? 0} tests</Typography>
                          <Typography variant="caption" sx={{ opacity: 0.7 }}>Findings: {totals.byType?.[k]?.findings ?? 0} • Avg Risk: {totals.byType?.[k]?.avgRisk != null ? Math.round(totals.byType[k].avgRisk) : '—'}</Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Card>
        )}

        {/* Charts */}
        <Grid container spacing={2}>
          {/* Avg Risk trend (filtered by type when chosen) */}
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 2.5, borderRadius: 3, height: 400 }}>
              <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>Avg Risk & Letter Grade Over Time</Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>Aggregated by {granularity}. Points labeled with letter grade. {typeFilter !== 'all' ? `Filtered: ${TYPE_META[typeFilter].label}` : 'All types'}.</Typography>
              <Box sx={{ height: 320 }}>{loading ? <Skeleton variant="rounded" height={320} /> : <Line data={avgRiskData} options={avgRiskOptions} />}</Box>
            </Card>
          </Grid>

          {/* Pentests per period (stacked by type when All) */}
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 2.5, borderRadius: 3, height: 400 }}>
              <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>Pentests Over Time {typeFilter === 'all' ? '(by Type)' : `(${TYPE_META[typeFilter].label})`}</Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>Created per {granularity}. {typeFilter === 'all' ? 'Stacked by type.' : 'Filtered to selected type.'}</Typography>
              <Box sx={{ height: 320 }}>{loading ? <Skeleton variant="rounded" height={320} /> : <Bar data={countsByTypeData} options={countsByTypeOptions} />}</Box>
            </Card>
          </Grid>

          {/* Findings per period (stacked by type when All) */}
          <Grid item xs={12}>
            <Card sx={{ p: 2.5, borderRadius: 3, height: 420 }}>
              <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>Findings Over Time {typeFilter === 'all' ? '(by Type)' : `(${TYPE_META[typeFilter].label})`}</Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>Total findings per {granularity}.</Typography>
              <Box sx={{ height: 340 }}>{loading ? <Skeleton variant="rounded" height={340} /> : <Bar data={findingsByTypeData} options={findingsByTypeOptions} />}</Box>
            </Card>
          </Grid>

          {/* Avg risk by type (overall) */}
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 2.5, borderRadius: 3, height: 380 }}>
              <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>Average Risk by Type</Typography>
              <Box sx={{ height: 300 }}>{loading ? <Skeleton variant="rounded" height={300} /> : <Bar data={avgRiskByType} options={avgRiskByTypeOptions} />}</Box>
            </Card>
          </Grid>

          {/* Findings by type (overall) */}
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 2.5, borderRadius: 3, height: 380 }}>
              <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>Findings by Type</Typography>
              <Box sx={{ height: 300 }}>{loading ? <Skeleton variant="rounded" height={300} /> : <Bar data={findingsByType} options={findingsByTypeOptions} />}</Box>
            </Card>
          </Grid>

          {/* Grade distribution */}
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 2.5, borderRadius: 3, height: 380 }}>
              <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>Grade Distribution</Typography>
              <Box sx={{ height: 300 }}>{loading ? <Skeleton variant="rounded" height={300} /> : <Doughnut data={gradeDistData} />}</Box>
            </Card>
          </Grid>

          {/* Risk vs Findings scatter */}
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 2.5, borderRadius: 3, height: 380 }}>
              <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>Risk vs Findings (by Pentest)</Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>Each point is a pentest. Color = type.</Typography>
              <Box sx={{ height: 300 }}>{loading ? <Skeleton variant="rounded" height={300} /> : <Scatter data={scatterData} options={scatterOptions} />}</Box>
            </Card>
          </Grid>
        </Grid>

        <Box sx={{ textAlign: 'right', mt: 3 }}>
          <Tooltip title="Back to Pentests"><IconButton onClick={() => navigate('/pentests')}><PublicIcon /></IconButton></Tooltip>
        </Box>
      </Box>
    </Box>
  );
};

export default PentestAnalytics;
